import { useState, useEffect } from "react";
import { usersAPI } from "../services/api";

// Custom hook for fetching users with pagination
export const useUsers = (page = 1, perPage = 10) => {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersAPI.getUsers(page, perPage);
      setUsers(response.users || []);
      setPagination(response.pagination || {});
    } catch (err) {
      setError(err.message);
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, perPage]);

  return {
    users,
    pagination,
    loading,
    error,
    refetch: fetchUsers,
  };
};

// Custom hook for user CRUD operations
export const useUserOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createUser = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersAPI.createUser(userData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersAPI.updateUser(userId, userData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersAPI.deleteUser(userId);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createUser,
    updateUser,
    deleteUser,
    loading,
    error,
  };
};

// Custom hook for user statistics
export const useUserStatistics = () => {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUserStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersAPI.getAllUserStatistics();
      setStatistics(response || []);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching user statistics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserStatistics();
  }, []);

  return {
    statistics,
    loading,
    error,
    refetch: fetchUserStatistics,
  };
};
